
import * as Sentry from "@sentry/vue";
import _ from "lodash";
import Vue from "vue";
import { VueTelInput } from "vue-tel-input";
import "vue-tel-input/dist/vue-tel-input.css";
import { mapActions, mapGetters, mapMutations } from "vuex";
import avsCheck, { IChangedFlags } from "~/assets/utils/Avs/avsCheck";
import areAllPropertiesFalse from "~/assets/utils/areAllPropertiesFalse";
import getAvsParams from "~/assets/utils/getAvsParams";
import highlightsDiff from "~/assets/utils/highlightsDiff";
import parseStateLabel from "~/assets/utils/parseStateLabel";
import Modal from "~/components/Modal.vue";
import LargeSelect from "~/components/form/LargeSelect.vue";
import orderSummary from "~/components/ship/orderSummary.vue";
import { AVSSubStatus, AVSStatus } from "~/entities/AVS";
import { Details, StageDetails } from "~/entities/Details";
import { Country, State } from "~/entities/Response/FormData";
import Validation from "~/entities/Validation";
import ValidationDetails from "~/entities/ValidationModules/ValidationDetails";

export default Vue.extend({
  name: "ShipDetails",
  components: {
    VueTelInput,
    LargeSelect,
    orderSummary,
    Modal,
  },
  layout: "WithServices",
  data() {
    return {
      PhoneValidationRules: {
        inputOptions: {
          placeholder: "",
          required: true,
          maxlength: 20,
        },
        validCharactersOnly: true,
        mode: "international",
        dropdownOptions: {
          disabled: false,
          showDialCodeInList: true,
          showDialCodeInSelection: false,
          showFlags: true,
          showSearchBox: true,
          tabindex: 0,
          width: "390px",
        },
      },
      stepNumber: 3,
      validation: new Validation(new ValidationDetails()),
      isOpen: false as boolean,
      isOpenResponsive: false,
      rendered: false,
      allowedCountries: [] as any,
      allowedStates: {} as any,
      stageDetails: [] as StageDetails[],
      updatedAddressModalTarget: null,
      modalOptions: null,
      updatedAddressModal: null,
      updatedDetails: [] as any,
      avsChecked: false,
      needsAVSModal: false,
      changedFlags: [] as { from: any; to: any }[],
      isCheckingInfo: false as boolean,
      isStateEditable: [] as { from: boolean; to: boolean }[],
      isSavedAddressDisabled: [] as { from: boolean; to: boolean }[],
      address2Required: [] as { to: boolean; from: boolean }[],
      isLoadingInput: [] as { to: boolean; from: boolean }[],
      needTriggerAvsInAddress2: false as boolean,
    };
  },
  computed: {
    // Vuex getters
    ...mapGetters({
      activeNotices: "errorsBag/activeNotices" as any,
      alertResponses: "errorsBag/alertResponses" as any,
      loading: "loading",
      boxes: "ship/boxes",
      trips: "ship/trips",
      purpose: "ship/purpose",
      rates: "ship/rates",
      details: "ship/details",
      selectedRateSpeedIndex: "ship/selectedRateSpeedIndex",
      premiumProtection: "ship/premiumProtection",
      subTotal: "payment/subTotal",
      ratesAreValid: "ship/ratesAreValid",
      currentEvent: "eventShipping/currentEvent",
      pickupOption: "ship/pickupOption",
      shipMode: "ship/shipMode",

      // formData
      countries: "formData/allowedCountries",
      states: "formData/allowedStates",
    }),
  },
  watch: {
    ratesAreValid(newVal) {
      if (newVal) {
        const result = newVal.event?.results;
        this.clearActiveNotices();
        // TODO: add new alerts
        if (result === "LocationChangedRequote" || result === "RateDiffValue") {
          (this as any).setError(newVal.event, "requote");
          // {name:'LocationChangedRequote', type:'address'}
          (this as any).setDetails(
            JSON.parse(JSON.stringify((this as any).stageDetails))
          );
          (this as any).setStep(3);
        } else if (!newVal.valid) {
          (this as any).setError(newVal, "rates");
          // (this as any).resetRequotes();
          (this as any).setStep(3);
        }
        if (newVal.valid) {
          for (let i = 0; i < this.stageDetails.length; i++) {
            if (this.stageDetails[i].from?.requote) {
              this.stageDetails[i].from.requote = false;
            }
            if (this.stageDetails[i].to?.requote) {
              this.stageDetails[i].to.requote = false;
            }
          }
          (this as any).resetRequotes();
        }
      }
    },
    "$auth.loggedIn"(newVal) {
      if (newVal) {
        // User has logged in
        for (let index = 0; index < this.stageDetails.length; index++) {
          Vue.set(this.isSavedAddressDisabled, index, {
            from: false,
            to: false,
          });
          this.stageDetails[index].from.needSaveAddress = false;
          this.stageDetails[index].to.needSaveAddress = false;
        }
      } else {
        for (let index = 0; index < this.stageDetails.length; index++) {
          Vue.set(this.isSavedAddressDisabled, index, {
            from: false,
            to: false,
          });
        }
      }
    },
    StageDetails: {
      handler() {
        if (this.stageDetails.length > 0) {
          for (const key in this.stageDetails) {
            const details: StageDetails = this.stageDetails[key];
            const updatedDetails: StageDetails = this.updatedDetails[key];
            if (
              (!details?.from.passedAvs && !updatedDetails?.from.passedAvs) ||
              (!details?.to.passedAvs && !updatedDetails?.to.passedAvs)
            ) {
              this.requestAlert({ name: "warningAVSFailed", type: "address" });
            } else {
              this.removeNotice({ name: "warningAVSFailed", type: "address" });
            }
          }
        }
      },
      deep: true,
    },
  },
  async mounted() {
    try {
      await this.fetchAllowedCountries(); // called only to make sure countries are present, since there should already be a country array in the store from the previous steps
      await this.fetchAllowedStates();
      this.allowedCountries = this.countries
        ?.map((el: Country) => {
          return {
            code: el.code as string,
            name: el.name as string,
            active: el.active as boolean,
            id: el.code as string,
            label: el.name as string,
          };
        })
        .filter((value) => value.active);
      this.mapStates(this.states);
      for (let step = 0; step < this.trips.length; step++) {
        Vue.set(this.stageDetails, step, {
          from: new Details(),
          to: new Details(),
        });
        Vue.set(this.address2Required, step, { from: false, to: false });
      }
      if (this.details.length) {
        for (let step = 0; step < this.details.length; step++) {
          this.isLoadingInput[step] = { from: false, to: false };
          if (this.stageDetails[step]) {
            if (this.stageDetails[step].from)
              this.stageDetails[step].from?.fillDetails(
                this.details[step]?.from
              );
            if (this.stageDetails[step].to)
              this.stageDetails[step].to?.fillDetails(this.details[step]?.to);

            if (!this.isStateEditable[step]) {
              this.isStateEditable[step] = {
                from:
                  this.details[step]?.from.searchType?.toLowerCase() ===
                    "partial" && !this.details[step]?.from.state,
                to:
                  this.details[step]?.to.searchType?.toLowerCase() ===
                    "partial" && !this.details[step]?.to.state,
              };
            }

            if (
              (!this.details[step]?.from?.passedAvs &&
                this.details[step].from.searchType !== "partial") ||
              (!this.details[step].to?.passedAvs &&
                this.details[step].to.searchType !== "partial")
            ) {
              this.requestAlert({ name: "warningAVSFailed", type: "address" });
            }

            this.isSavedAddressDisabled[step] = {
              from: false,
              to: false,
            };
            if (
              this.stageDetails[step].from?.addressId ||
              !this.$auth.loggedIn
            ) {
              this.isSavedAddressDisabled[step].from = true;
            }
            if (this.stageDetails[step].to?.addressId || !this.$auth.loggedIn) {
              this.isSavedAddressDisabled[step].to = true;
            }
          }
        }
      }
      this.fillStageDetails();
      this.rendered = true;
      this.validation.module.component = this;
      Object.assign(this.updatedDetails, this.stageDetails);
      this.updatedDetails = _.cloneDeep(this.stageDetails);
      (this as any).validation.module.validatePackageSelection();
      this.updateTripsFormattedAddressLabels();
      window.scrollTo(0, 0);
      Sentry.addBreadcrumb({
        message: "Threshold: details.",
        data: { rates: JSON.stringify(this.rates), subtotal: this.subTotal },
        category: "details",
      });
      if (typeof window !== "undefined" && window.gtag) {
        const route = this.$route.name;
        let event = null;
        if (
          route === "events-event" ||
          route === "event" ||
          route === "partners-event"
        ) {
          event = this.currentEvent?.name
            ? this.currentEvent.name
            : this.$route.params.event;
        }
        window.gtag("event", "page_impression", {
          pagePath: this.$route.path,
          pageTitle: event
            ? "Ship - " + event + " - Details"
            : "Ship - Details",
          visitorType: this.$auth.loggedIn ? "customer" : "guest",
        });
      }
      (this as any).validation.module.ValidateAddress2();
    } catch (e) {
      console.log(e);
    }
  },
  methods: {
    highlightsDiff,
    // Vuex actions
    ...mapMutations({
      setLoading: "setLoading",
      setBoxes: "ship/setBoxes",
      setTrips: "ship/setTrips",
      setUnits: "ship/setUnits",
      setPurpose: "ship/setPurpose",
      setShipMode: "ship/setShipMode",
      setDetails: "ship/setDetails",
      updateTrips: "ship/updateTrips",
      setDetailsOnly: "ship/setDetailsOnly",
      cleanBoxErrors: "ship/cleanBoxErrors",
      resetRequotes: "ship/resetRequotes",
      updateTripsFormattedAddressLabels:
        "ship/updateTripsFormattedAddressLabels",
      updateTripsLabelsAfterAddressReplace:
        "ship/updateTripsLabelsAfterAddressReplace",
    }),
    ...mapActions({
      getRates: "ship/getRates",
      calculateSubTotal: "payment/calculateSubTotal",
      requestRates: "ship/requestRates",
      requestAlert: "errorsBag/requestAlert",
      clearActiveNotices: "errorsBag/clearActiveNotices",
      clearAlertResponses: "errorsBag/clearAlertResponses",
      removeNotice: "errorsBag/removeNotice",
      addNotice: "errorsBag/addNotice",
      setStep: "ship/setStep",
      requestAVSCheck: "ship/requestAVSCheck",
      requestAVSUnitCheck: "ship/requestAVSUnitCheck",
      logRequest: "ship/logRequest",
      requestResolve: "ship/requestResolve",

      // formData
      fetchAllowedCountries: "formData/fetchAllowedCountries",
      fetchAllowedStates: "formData/fetchAllowedStates",
      // create/edit new saved address
      createAddress: "addressRequest/createAddressRequest",
      editSavedAddress: "addressRequest/editAddressRequest",
    }),
    countrySelectionMade(selection: any) {
      this.rendered = false;
      const elements = selection.name.split("-");

      if (elements.length > 1) {
        const point = elements[1];
        const index = elements[elements.length - 1];
        // if difference is found
        if (
          selection.option &&
          this.stageDetails[index][point].country &&
          selection.option.code !== this.stageDetails[index][point].country.code
        ) {
          this.onRequote(index, point);
        }
        this.stageDetails[index][point].country = selection.option;
        // TODO: this maybe problematic, but is for a dev of the future
        // this.stageDetails[index][point].state = null;
        this.updatedDetails[index][point].country = selection.option;
        // this.updatedDetails[index][point].state = null;
      }
      this.rendered = true;
    },
    checkCountryDefault(event) {
      if (
        !!event?.country?.code &&
        event.phone.length > 3 &&
        !event.phone.includes("+")
      ) {
        return event.country.code;
      }
    },
    stateSelectionMade(selection: any) {
      this.rendered = false;
      const elements = selection.name.split("-");
      if (elements.length > 1) {
        const point = elements[1];
        const index = elements[elements.length - 1];
        try {
          // if difference is found
          if (
            selection.option &&
            this.stageDetails[index][point]?.state &&
            selection.option.stateprovcode !==
              this.stageDetails[index][point]?.state?.stateprovcode
          ) {
            this.onRequote(index, point);
          }
          this.stageDetails[index][point].state = selection.option;
          this.updatedDetails[index][point].state = selection.option;
        } catch (e) {
          this.rendered = true;
        }
      }
      this.rendered = true;
    },
    updateFromAttnOption(option: any) {
      this.stageDetails[option.index].from.address3 = option.val
        ? option.val.trim()
        : "";
      this.onUpdate();
    },
    updateToAttnOption(option: any) {
      this.stageDetails[option.index].to.address3 = option.val
        ? option.val.trim()
        : "";
      this.onUpdate();
    },
    fillStageDetails() {
      for (let step = 0; step < this.trips.length; step++) {
        // check if details has data and fill stageDetails with it
        if (!this.details[step]?.from?.passedAvs) {
            this.stageDetails[step].from.setStage(
            this.trips[step].from,
            this.allowedCountries,
            this.allowedStates
          );
        }
        if (!this.details[step]?.to?.passedAvs) {
          this.stageDetails[step].to.setStage(
            this.trips[step].to,
            this.allowedCountries,
            this.allowedStates
          );
        }
        if (
          this.details?.[step]?.from?.postalCode.includes(
            this.stageDetails[step].from.postalCode
          )
        ) {
          this.stageDetails[step].from.postalCode =
            this.details?.[step]?.from?.postalCode;
        }
        if (
          this.details?.[step]?.to?.postalCode.includes(
            this.stageDetails[step].to.postalCode
          )
        ) {
          this.stageDetails[step].to.postalCode =
            this.details?.[step]?.to?.postalCode;
        }
        this.stageDetails[step].from.searchType =
          this.details?.[step]?.from?.searchType;
        this.stageDetails[step].to.searchType =
          this.details?.[step]?.to?.searchType;
      }
    },
    mapStates(states: Array<State>) {
      try {
        const _this = this;
        for (let step = 0; step < states.length; step++) {
          const country = states[step].country;
          const countryStates = _this.allowedStates[country];
          if (
            typeof countryStates === "undefined" ||
            (countryStates && countryStates.length === 0)
          ) {
            _this.allowedStates[country] = [];
          }
          const state = {} as State;
          Object.assign(state, states[step]);
          state.id = state.stateprovcode;
          state.label = state.stateprovcode + " - " + state.stateprov;
          _this.allowedStates[country].push(state);
        }
        return true;
      } catch (e) {
        return false;
      }
    },
    validatePhone(eventData: any, target: any) {
      // Validate the input element within the vue-tel-input component
      target.phoneValid = !!eventData.valid;
      if (target.phoneValid) {
        this.onUpdate();
      }
    },
    setError(e, type: any = "") {
      // example: called from stageForm e = {} as ratesAreValid | boolean, type = 'requote'
      // call to request error on validation object
      this.validation.module.requestAlert({ event: e, type });
    },
    onUpdate() {
      try {
        const details = _.cloneDeep(this.stageDetails);
        this.setDetailsOnly(details);
        if (this.$refs.orderSummary) {
          (this as any).$refs.orderSummary.reRender();
        }
      } catch (e) {
        console.log(e);
      }
    },
    onRequote(index: number, direction: string) {
      if (
        !this.stageDetails[index][direction].postalCode.includes(
          this.details?.[0]?.[direction]?.postalCode
        )
      ) {
        this.stageDetails[index][direction].requote = true;
      }
      this.setDetails(JSON.parse(JSON.stringify(this.stageDetails)));
      this.requestRates({ stepName: "Details", stepNumber: 3 });
    },
    async validateStep() {
      if (
        (this as any).validation.module.validating === false &&
        (await (this as any).validation.Validate())
      ) {
        (this as any).validation.module.validating = true;
        // let needsAVSModal = false;
        // await this.resolveAddress();
        let allPropertiesAreFalse = true;
        for (let index = 0; index < this.changedFlags.length; index++) {
          if (
            !areAllPropertiesFalse(this.changedFlags[index].from) ||
            !areAllPropertiesFalse(this.changedFlags[index].to)
          ) {
            // show modal
            allPropertiesAreFalse = false;
          }
        }
        await new Promise((resolve) => {
          setTimeout(() => {
            resolve(true);
          }, 1000);
        });

        if (!this.avsChecked) {
          if (!allPropertiesAreFalse) {
            // show modal
            this.isOpen = true;
            return;
          } else {
            await this.goToNextStep();
          }
        }
        if (allPropertiesAreFalse || this.avsChecked) {
          await this.goToNextStep();
        }
      }
      (this as any).validation.module.validating = false;
    },
    async goToNextStep() {
      await this.saveAddress();
      this.updateStage(false);
      const cloneStageDetails = _.cloneDeep(this.stageDetails);
      const detailsArray = cloneStageDetails.map((details: StageDetails) => {
        return { from: details.from, to: details.to };
      });
      this.setDetails(detailsArray);
      this.logRequest(window ? window.navigator.userAgent : "");
      this.setStep(4);
      (this as any).validation.module.validating = false;
    },
    async resolveAddress() {
      let index: number = 0;
      try {
        for (const details of this.stageDetails) {
          const toObj = this.parseResolveBodyRequest(details.to);
          const formattedAddressToObj = this.trips[index].to.formatted_address;
          const companyNameTo: string = this.details[index].to.company;
          const fromObj = this.parseResolveBodyRequest(details.from);
          const formattedAddressFromObj =
            this.trips[index].from.formatted_address;
          const companyNameFrom: string = this.details[index].from.company;
          const resolvedLocationFrom = await this.requestResolve({
            address: fromObj,
            originalAddress: formattedAddressToObj,
            company: companyNameFrom,
            type: 3,
          });
          const resolvedLocationTo = await this.requestResolve({
            address: toObj,
            originalAddress: formattedAddressFromObj,
            company: companyNameTo,
            type: 3,
          });
          if (resolvedLocationFrom) {
            this.updateTripsLabelsAfterAddressReplace({
              index,
              resolve: resolvedLocationFrom,
              type: "from",
            });
            this.reverseResolvedAddressComponents(
              resolvedLocationFrom,
              this.stageDetails[index].from
            );
          }
          if (resolvedLocationTo) {
            this.updateTripsLabelsAfterAddressReplace({
              index,
              resolve: resolvedLocationTo,
              type: "to",
            });
            this.reverseResolvedAddressComponents(
              resolvedLocationTo,
              this.stageDetails[index].to
            );
          }
          index += 1;
        }
        return true;
      } catch (error) {
        console.error(error);
      }
    },
    parseResolveBodyRequest(details: any) {
      return {
        streetAddress: details.streetAddress ? details.streetAddress : "",
        address1: details.address1 ? details.address1 : "",
        address2: details.address2 ? details.address2 : "",
        city: details.city ? details.city : "",
        country: details.country ? details.country : "",
        countryCode: details.country ? details.country?.code : "",
        postalCode: details.postalCode ? details.postalCode : "",
        state: details.state ? details.state.stateprovcode : "",
      };
    },
    reverseResolvedAddressComponents(resolvedAddress, detail: Details) {
      if (resolvedAddress) {
        if (resolvedAddress.withAddress1) {
          detail.address1 = resolvedAddress.withAddress1;
          detail.streetAddress = resolvedAddress.withAddress1;
          // add to place address components
        }
        if (resolvedAddress.withAddress2) {
          detail.address2 = resolvedAddress.withAddress2;
        }
        if (resolvedAddress.withCity) {
          detail.city = resolvedAddress.withCity;
          // add to place address components
        }
        if (resolvedAddress.withPostalCode) {
          detail.postalCode = resolvedAddress.withPostalCode;
          // add to place address components
        }
        if (
          typeof resolvedAddress?.withResidential === "boolean" &&
          resolvedAddress?.withResidential != null
        ) {
          detail.isResidential = resolvedAddress.withResidential;
        }
        if (resolvedAddress?.withPassedAvs) {
          detail.passedAvs = resolvedAddress.withPassedAvs;
          if (resolvedAddress.withPassedAvs) {
            detail.avsStatus.status = AVSStatus.success;
            detail.avsStatus.subStatus = AVSSubStatus.onAddressReplace;
          }
        }
        if (resolvedAddress.withState) {
          detail.state = {
            id: resolvedAddress.withState,
            stateprovcode: resolvedAddress.withState,
            stateprov: resolvedAddress.withState,
            country: resolvedAddress.withCountry,
            label: resolvedAddress.withState,
          };
        }
        if (resolvedAddress.withCountry) {
          detail.country = this.allowedCountries.find(
            (country) => country.code === resolvedAddress.withCountry
          );
        }
      }
    },
    detailChanged(direction, step) {
      (this as any).validation.module.validateAddressFields();
      (this as any).validation.module.validatePackageSelection();
      (this as any).avsChecked = false;
      this.checkChangeDetails(step, direction);
    },
    checkChangeDetails(step: number, direction: string): void {
      const changed =
        this.updatedDetails?.[step]?.[direction].streetAddress !==
        this.stageDetails[step][direction].streetAddress;
      if (changed && this.stageDetails[step][direction].isSavedAddress) {
        this.stageDetails[step][direction].avsStatus.subStatus =
          AVSSubStatus.modifiedByUser;
      }
    },
    hideModal() {
      this.isOpen = false;
      this.avsChecked = true;
      this.requestAlert({ name: "warningAVSFailed", type: "address" });
      this.validation.module.validating = false;
      this.setRejectedEnumState();
    },
    setRejectedEnumState() {
      for (const stageDetail of this.stageDetails) {
        if (!stageDetail.from.isSavedAddress && !stageDetail.from.passedAvs) {
          stageDetail.from.avsStatus.status = AVSStatus.failed;
          stageDetail.from.avsStatus.subStatus = AVSSubStatus.rejectedByUser;
        }
        if (!stageDetail.to.isSavedAddress && !stageDetail.to.passedAvs) {
          stageDetail.to.avsStatus.status = AVSStatus.failed;
          stageDetail.to.avsStatus.subStatus = AVSSubStatus.rejectedByUser;
        }
      }
    },
    async completeModal() {
      if ((this as any).isOpen) {
        (this as any).updateStage(true);
        (this as any).isOpen = false;
        for (let i = 0; i < this.stageDetails.length; i++) {
          if (
            this.stageDetails[i].from.isChanged &&
            this.stageDetails[i].from.isSavedAddress
          ) {
            const detailEdited = _.cloneDeep(this.stageDetails[i].from);
            detailEdited.state = detailEdited.state
              ? detailEdited.state.stateprovcode
              : "";
            detailEdited.country = detailEdited.country
              ? detailEdited.country.code
              : "";
            await this.editSavedAddress(detailEdited);
          }
          if (
            this.stageDetails[i].to.isChanged &&
            this.stageDetails[i].to.isSavedAddress
          ) {
            const detailEdited = _.cloneDeep(this.stageDetails[i].to);
            detailEdited.state = detailEdited.state
              ? detailEdited.state.stateprovcode
              : "";
            detailEdited.country = detailEdited.country
              ? detailEdited.country.code
              : "";
            await this.editSavedAddress(detailEdited);
          }
        }
        (this as any).setDetails(
          JSON.parse(JSON.stringify((this as any).stageDetails))
        );
        (this as any).logRequest(window ? window.navigator.userAgent : "");
        (this as any).setStep(4);
      }
      (this as any).validation.module.validating = false;
    },
    parseToUppercase(item: any) {
      for (const key in item) {
        if (typeof item[key] === "string") item[key] = item[key].toUpperCase();
      }
      return item;
    },
    checkIfDetailUpdated(updatedDetailNoParsed, stageDetailNoParsed) {
      const updatedDetail: any = this.parseToUppercase(
        _.cloneDeep(updatedDetailNoParsed)
      );
      const stageDetail: any = this.parseToUppercase(
        _.cloneDeep(stageDetailNoParsed)
      );
      const isDifferent = (a, b) => {
        return a && b && a.trim() !== b.trim();
      };
      return (
        isDifferent(updatedDetail.company, stageDetail.company) ||
        isDifferent(updatedDetail.streetAddress, stageDetail.streetAddress) ||
        isDifferent(updatedDetail.address2, stageDetail.address2) ||
        isDifferent(updatedDetail.city, stageDetail.city) ||
        isDifferent(
          updatedDetail.state?.stateprovcode,
          stageDetail.state?.stateprovcode
        ) ||
        isDifferent(updatedDetail.postalCode, stageDetail.postalCode) ||
        isDifferent(updatedDetail.country?.code, stageDetail.country?.code)
      );
    },
    updateStage(avsFailed = true) {
      // here, check here if it is being added
      const updateProperty = (source, target, property) => {
        if (typeof source[property] === "string") {
          target[property] = source[property].trim();
        } else if (source[property] !== undefined) {
          target[property] = source[property];
        }
      };
      for (let step = 0; step < (this as any).stageDetails.length; step++) {
        const stageDetail = this.stageDetails[step];
        const updatedDetail = this.updatedDetails[step];
        const hasPickup = this.pickupOption[step].hasPickup; // used to avoid edit the from when pìckup option is dropoff.
        const properties = [
          "company",
          "address1",
          "streetAddress",
          "address2",
          "address3",
          "city",
          "state",
          "postalCode",
          "countryCode",
          "isResidential",
          "success",
          "passedAvs",
          "errorCode",
          "errorMessage",
          "isChanged",
          "validationLevel",
          "validationSource",
          "avsStatus",
          "avsSubStatus",
          "avsStatus",
        ];
        if (avsFailed && stageDetail && updatedDetail) {
          ["from", "to"].forEach((key) => {
            const isValidToUpdate =
              (!hasPickup && key === "to") || hasPickup || step > 0;
            const isValidSavedAddress =
              stageDetail[key].isSavedAddress && stageDetail[key].passedAvs;
            const isValidEventAddress =
              stageDetail[key].isEventAddress && stageDetail[key].passedAvs;
            if (
              stageDetail[key] &&
              updatedDetail[key] &&
              isValidToUpdate &&
              !isValidSavedAddress &&
              !isValidEventAddress
            ) {
              properties.forEach((property) =>
                updateProperty(updatedDetail[key], stageDetail[key], property)
              );
            }
          });
        } else if (updatedDetail.from && stageDetail.from) {
          properties.forEach((property) =>
            updateProperty(stageDetail.from, updatedDetail.from, property)
          );
        }
      }
    },
    checkIfAddressIsValid(stageDetail: Details, property: string) {
      if (stageDetail.searchType?.toLowerCase() === "full") {
        return true;
      }
      if (
        stageDetail.searchType?.toLowerCase() === "partial" &&
        (property === "state" || property === "country")
      ) {
        return true;
      }
      return false;
    },
    async checkAvsOnInput(event, direction: string, index: number) {
      try {
        if (
          event.relatedTarget === this.$refs.fromAddress2?.[index] ||
          event.relatedTarget === this.$refs.toAddress2?.[index]
        ) {
          this.needTriggerAvsInAddress2 = true;
          return;
        }

        const detail = _.cloneDeep(this.stageDetails[index][direction]);
        if (
          (detail.streetAddress || detail.address1) &&
          detail.city &&
          detail.postalCode &&
          detail.country
        ) {
          this.isCheckingInfo = true;
          this.isLoadingInput[index][direction] = true;
          const fromObj = this.parseResolveBodyRequest(detail);
          const formattedAddressFromObj =
            this.trips[index][direction].formatted_address;
          const companyNameFrom: string = detail.company;
          const resolvedLocationFrom = await this.requestResolve({
            address: fromObj,
            originalAddress: formattedAddressFromObj,
            company: companyNameFrom,
            type: 3,
          });
          if (resolvedLocationFrom) {
            this.updateTripsLabelsAfterAddressReplace({
              index,
              resolve: resolvedLocationFrom,
              type: direction,
            });
            this.reverseResolvedAddressComponents(
              resolvedLocationFrom,
              this.stageDetails[index][direction]
            );
          }
          const fromDetailsValidatedByAVS: {
            stageDetails: Details;
            updatedDetails: Details;
            changedFlags: IChangedFlags;
            error?: string;
          } = await avsCheck(this.stageDetails[index][direction]);
          const newDetails = fromDetailsValidatedByAVS.stageDetails;

          if (typeof newDetails.country === "string") {
            for (let step = 0; step < this.allowedCountries.length; step++) {
              if (this.allowedCountries[step].code === newDetails.country) {
                Vue.set(newDetails, "country", this.allowedCountries[step]);
              }
            }
          }
          if (typeof newDetails.state === "string") {
            for (
              let step = 0;
              step < this.allowedStates[newDetails.country.code].length;
              step++
            ) {
              if (
                newDetails.state ===
                this.allowedStates[newDetails.country.code][step].stateprovcode
              ) {
                Vue.set(
                  newDetails,
                  "state",
                  this.allowedStates[newDetails.country.code][step]
                );
              }
            }
          }
          if (!areAllPropertiesFalse(fromDetailsValidatedByAVS.changedFlags)) {
            this.stageDetails[index][direction].passedAvs = false;
            this.updatedDetails[index][direction] =
              fromDetailsValidatedByAVS.updatedDetails;
          } else {
            this.stageDetails[index][direction].passedAvs = true;
            this.updatedDetails[index][direction] =
              fromDetailsValidatedByAVS.stageDetails;
          }
          if (!this.changedFlags[index]) {
            this.changedFlags[index] = { from: {}, to: {} };
          }
          this.changedFlags[index][direction] =
            fromDetailsValidatedByAVS.changedFlags;
          if (event.relatedTarget === this.$refs.validateButton) {
            this.validateStep();
          }
          Vue.set(this.stageDetails[index], direction, newDetails);
          this.isCheckingInfo = false;
          this.isLoadingInput[index][direction] = false;
        }
      } catch (error) {
        this.isCheckingInfo = false;
        this.isLoadingInput[index][direction] = false;
        console.log(error);
      }
    },
    async saveAddress(): Promise<boolean> {
      for (let index = 0; index < this.stageDetails.length; index++) {
        if (this.stageDetails[index].from.needSaveAddress) {
          const bodyRequest = _.cloneDeep(this.stageDetails[index].from);
          bodyRequest.country = bodyRequest.country.code;
          bodyRequest.state =
            typeof bodyRequest.state === "string"
              ? bodyRequest.state
              : bodyRequest.state.stateprovcode;
          bodyRequest.displayName =
            (bodyRequest.company
              ? bodyRequest.company + " "
              : bodyRequest.firstName + " " + bodyRequest.lastName + " ") +
            bodyRequest.postalCode;
          if (bodyRequest.addressId) {
            await this.editSavedAddress(bodyRequest);
          } else {
            const response = await this.createAddress(bodyRequest);
            this.stageDetails[index].from.addressId = response.addressId;
          }
        }
        if (this.stageDetails[index].to.needSaveAddress) {
          const bodyRequest = _.cloneDeep(this.stageDetails[index].to);
          bodyRequest.country = bodyRequest.country.code;
          bodyRequest.state =
            typeof bodyRequest.state === "string"
              ? bodyRequest.state
              : bodyRequest.state.stateprovcode;
          bodyRequest.displayName =
            (bodyRequest.company
              ? bodyRequest.company + " "
              : bodyRequest.firstName + " " + bodyRequest.lastName + " ") +
            bodyRequest.postalCode;
          if (bodyRequest.addressId) {
            await this.editSavedAddress(bodyRequest);
          } else {
            const response = await this.createAddress(bodyRequest);
            this.stageDetails[index].to.addressId = response.addressId;
          }
        }
      }
      return true;
    },
    async checkIfAVSRequireAddress2(): Promise<
      { to: boolean; from: boolean }[]
    > {
      for (let index = 0; index < this.stageDetails.length; index++) {
        const fromNeedAddress2 = await this.requestAVSUnitCheck(
          getAvsParams(this.stageDetails[index].from)
        );
        if (fromNeedAddress2) {
          this.address2Required[index].from = true;
        } else {
          this.address2Required[index].from = false;
        }
        const toNeedAddress2 = await this.requestAVSUnitCheck(
          getAvsParams(this.stageDetails[index].to)
        );
        if (toNeedAddress2) {
          this.address2Required[index].to = true;
        } else {
          this.address2Required[index].to = false;
        }
      }
      return this.address2Required;
    },
    chainedAvsCheck(event, direction: string, index: number) {
      if (this.needTriggerAvsInAddress2) {
        this.checkAvsOnInput(event, direction, index);
        this.needTriggerAvsInAddress2 = false;
      }
    },
    parseStateLabel,
  },
  fetchOnServer: false,
});
